import { fromJS, Record } from 'immutable';
import { getProperty, getPropertyLabel, getReferenceLabel, getReference, getPropertyType } from 'reporting-data/v2/dataset/datasetTypes';
import { NUMBER, CURRENCY, PERCENT } from 'reporting-data/constants/property-types';
import { DEFAULT_DEBUG } from '../../constants/debug';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module '../.... Remove this comment to see the full error message
import { getDefaultOptions } from '../../highcharts/options/defaultOptions';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module '../.... Remove this comment to see the full error message
import { getLegendPositionOptions } from '../../highcharts/options/legendOptions';
import { defaultGaugeSeriesOptions } from '../../highcharts/options/series/gaugeSeriesOptions';
import { GaugeDataLabel } from './GaugeDataLabel';
import { getPropertyFormatter, isFormattableProperty
//@ts-expect-error Untyped dependency
} from 'reporting-visualizations/formatters/propertyFormatter';
import { BATTLESHIP, CANDY_APPLE, GREAT_WHITE, OBSIDIAN } from 'reporting-visualizations/constants/hubStyleTokensColors';
import { renderToString } from 'react-dom/server';
import { GaugeTooltip } from './GaugeTooltip';
import I18n from 'I18n';
import { getPropertyFormatterOverrides } from '../../formatters/propertyFormatterUtils';
import { mapInputValueToUsableValue } from '../../utils/valueMappingUtils';
import { shouldInverseDataRows } from './GaugeUtils';
import { ThemeProvider } from 'foundations-theming/components/ThemeProvider';
import { jsx as _jsx } from "react/jsx-runtime";
const PRIMARY_SERIES_DEFAULT_COLOR = OBSIDIAN;
const COMPARE_SERIES_DEFAULT_COLOR = CANDY_APPLE;
const REFERENCE_LINE_SERIES_DEFAULT_COLOR = BATTLESHIP;
const BAND_THICKNESS_DECIMAL_PERCENTAGE = 0.2;
const BAND_THICKNESS_PERCENTAGE = `${BAND_THICKNESS_DECIMAL_PERCENTAGE * 100}%`;
const isFiniteNumber = value => {
  return typeof value === 'number' && Number.isFinite(value);
};
const convertNumberIfNecessary = (dataset, propertyKey, number, valueFormattingOptions) => {
  const property = getProperty(dataset, propertyKey);
  const numberDisplayHint = (property === null || property === void 0 ? void 0 : property.numberDisplayHint) || (property === null || property === void 0 ? void 0 : property.type);
  if (propertyKey && numberDisplayHint) {
    const mappedNumber = mapInputValueToUsableValue(number, numberDisplayHint, valueFormattingOptions);
    if (mappedNumber !== undefined) {
      return mappedNumber;
    }
  }
  return number;
};
const getGaugePropertyFormatter = (property, abbreviateIfPossible) => {
  const propertyMap = fromJS(property);
  const propertyType = propertyMap.get('type');
  const propertyFormatterOverrides = getPropertyFormatterOverrides(propertyType, abbreviateIfPossible);
  const propertyFormatter = getPropertyFormatter(propertyMap, propertyFormatterOverrides);
  return propertyFormatter;
};
const getPropertyWithFormattingOptions = (dataset, propertyKey, valueFormattingOptions) => {
  const maybeFormattableProperty = fromJS(getProperty(dataset, propertyKey));
  return maybeFormattableProperty && isFormattableProperty(maybeFormattableProperty) && valueFormattingOptions ? maybeFormattableProperty.set('formattingType', valueFormattingOptions.format.type).set('formattingObject', Record(valueFormattingOptions.format)()) // downstream code expects a Record specifically.
  : maybeFormattableProperty;
};
const getFormattedDataValue = (dataset, propertyKey, propertyValue, valueFormattingOptions, abbreviateIfPossible) => {
  const propertyType = getPropertyType(dataset, propertyKey);
  const isNumericProperty = propertyType && (propertyType === NUMBER || propertyType === CURRENCY || propertyType === PERCENT);
  if (!(abbreviateIfPossible && isNumericProperty)) {
    const reference = getReference(dataset, propertyKey, propertyValue);
    if (reference) {
      return getReferenceLabel(dataset, propertyKey, propertyValue, propertyValue);
    }
  }
  const formattableProperty = getPropertyWithFormattingOptions(dataset, propertyKey, valueFormattingOptions);
  if (formattableProperty) {
    const propertyFormatter = getGaugePropertyFormatter(formattableProperty, abbreviateIfPossible);
    return propertyFormatter(propertyValue);
  }
  return propertyValue;
};
const extractDataValuesAndLabels = (data, column, valueFormattingOptions, compareColumn, abbreviateValuesIfPossible) => {
  var _data$compare, _data$compare2;
  const buildValueAndLabels = ({
    dataRow,
    dataset
  }) => {
    const dataValue = dataRow[column];
    const formattedDataValue = getFormattedDataValue(dataset, column, dataValue, valueFormattingOptions, abbreviateValuesIfPossible);
    const dataValueLabel = getPropertyLabel(dataset, column);
    return {
      dataValue,
      formattedDataValue,
      dataValueLabel,
      dataRow,
      dataset
    };
  };

  // Gauge is a single value visualization, but can have multiple series (e.g. compare).
  // For Snowflake Currently all data is provided in the primary dataset and represented as additional
  // rows.
  // For config reports, the compared data is provided in the compare dataset.
  // In the case there are multiple rows in the primary dataset and there is a compare dataset,
  // we will get the compared value from the compared dataset

  const hasCompareDataSet = !!((_data$compare = data.compare) !== null && _data$compare !== void 0 && _data$compare.data) && ((_data$compare2 = data.compare) === null || _data$compare2 === void 0 || (_data$compare2 = _data$compare2.data) === null || _data$compare2 === void 0 ? void 0 : _data$compare2.length) > 0;
  const hasCompareDataFromPrimaryDataSet = data.primary.data.length > 1 && compareColumn;
  if (hasCompareDataSet) {
    var _data$compare3;
    // this is the case where the "compare" comes in a separate dataset (ie: SO report)
    const primaryDataRow = data.primary.data[0];
    const primaryDataset = data.primary;
    const compareDataRow = (data === null || data === void 0 || (_data$compare3 = data.compare) === null || _data$compare3 === void 0 ? void 0 : _data$compare3.data[0]) || [];
    const compareDataset = data.compare;
    return {
      primary: Object.assign({}, buildValueAndLabels({
        dataRow: primaryDataRow,
        dataset: primaryDataset
      })),
      compare: compareDataset && Object.assign({}, buildValueAndLabels({
        dataRow: compareDataRow,
        dataset: compareDataset
      }), {
        compareValueLabel: getPropertyLabel(compareDataset, column),
        datasetName: 'compare'
      })
    };
  } else {
    let primaryDataRow;
    let compareDataRow;
    if (hasCompareDataFromPrimaryDataSet) {
      if (shouldInverseDataRows({
        data,
        compareColumn
      })) {
        primaryDataRow = data.primary.data[0];
        compareDataRow = data.primary.data[1];
      } else {
        primaryDataRow = data.primary.data[1];
        compareDataRow = data.primary.data[0];
      }
    } else {
      primaryDataRow = data.primary.data[0];
    }
    return {
      primary: Object.assign({}, buildValueAndLabels({
        dataRow: primaryDataRow,
        dataset: data.primary
      }), {
        compareValueLabel: compareColumn && getReferenceLabel(data.primary, compareColumn, primaryDataRow[compareColumn])
      }),
      compare: compareDataRow && compareColumn ? Object.assign({}, buildValueAndLabels({
        dataRow: compareDataRow,
        dataset: data.primary
      }), {
        compareValueLabel: getReferenceLabel(data.primary, compareColumn, compareDataRow[compareColumn])
      }) : undefined
    };
  }
};
const getDataValuesFromDataValuesAndLabels = dataValuesAndLabels => {
  const dataValues = [dataValuesAndLabels.primary.dataValue];
  if (dataValuesAndLabels.compare) {
    dataValues.push(dataValuesAndLabels.compare.dataValue);
  }
  return dataValues;
};
export const getDataLabelFormatter = (data, dataColumn, valueFormattingOptions, compareColumn) => function () {
  const yAxisElement = this.series.chart.container.querySelector('.highcharts-yaxis');
  const gaugeWidth = yAxisElement === null || yAxisElement === void 0 ? void 0 : yAxisElement.getBoundingClientRect().width;
  const gaugeWidthExcludingBands = gaugeWidth ? gaugeWidth * (1 - BAND_THICKNESS_DECIMAL_PERCENTAGE) * 0.95 : undefined;
  const dataAndLabels = extractDataValuesAndLabels(data, dataColumn, valueFormattingOptions, compareColumn, true);
  const primaryDataAndLabels = dataAndLabels.primary;
  const compareDataAndLabels = dataAndLabels.compare;
  return renderToString( /*#__PURE__*/_jsx(ThemeProvider, {
    children: /*#__PURE__*/_jsx(GaugeDataLabel, {
      dataValue: primaryDataAndLabels.formattedDataValue,
      dataValueLabel: primaryDataAndLabels.dataValueLabel,
      compareValue: compareDataAndLabels ? compareDataAndLabels.formattedDataValue : undefined,
      chartWidth: this.series.chart.chartWidth,
      gaugeWidth: gaugeWidthExcludingBands
    })
  }));
};
export const getTooltipFormatter = (data, dataColumn, valueFormattingOptions) => function () {
  const tickValue = this.y;
  const seriesLabel = this.series && this.series.name;
  const formattedDataValue = getFormattedDataValue(data.primary, dataColumn, tickValue, valueFormattingOptions, false);
  return renderToString( /*#__PURE__*/_jsx(GaugeTooltip, {
    dataValue: formattedDataValue,
    dataValueLabel: seriesLabel
  }));
};
export const getAxisLabelFormatter = (data, dataColumn, valueFormattingOptions) => function () {
  const formattableProperty = getPropertyWithFormattingOptions(data, dataColumn, valueFormattingOptions);
  if (formattableProperty) {
    const propertyFormatter = getGaugePropertyFormatter(formattableProperty, true);
    return propertyFormatter(this.value);
  }
  return this.value;
};

/*
 * Calculate the range for the gauge chart.
 * Overrides user specified max value with max data value to ensure the data renders properly.
 */
const getGaugeRange = (layer, data, referenceLineValues) => {
  const {
    valueFormattingOptions,
    dataColumn,
    compareColumn,
    plot
  } = layer;
  const getMinOrMaxReferenceLineValue = (minOrMax, lineValues) => {
    if (lineValues && lineValues.length > 0) {
      const lines = lineValues.filter(lineValue => isFiniteNumber(lineValue.value)).map(lineValue => lineValue.value);
      return minOrMax(...lines);
    }
    return undefined;
  };
  const maxReferenceLineValue = getMinOrMaxReferenceLineValue(Math.max, referenceLineValues);
  const minReferenceLineValue = getMinOrMaxReferenceLineValue(Math.min, referenceLineValues);
  const min = convertNumberIfNecessary(data.primary, layer.dataColumn, (plot === null || plot === void 0 ? void 0 : plot.min) || 0, valueFormattingOptions);
  let targetColumnValue;
  if (layer.targetColumn) {
    const targetColumnDataAndLabels = extractDataValuesAndLabels(data, layer.targetColumn, valueFormattingOptions, compareColumn);
    targetColumnValue = Math.max(...getDataValuesFromDataValuesAndLabels(targetColumnDataAndLabels), 0);
  }
  const max = convertNumberIfNecessary(data.primary, layer.targetColumn || layer.dataColumn, targetColumnValue || (plot === null || plot === void 0 ? void 0 : plot.max) || 5, valueFormattingOptions);
  const dataAndLabels = extractDataValuesAndLabels(data, dataColumn, valueFormattingOptions, compareColumn);

  // Get the max of user specified max value or the max value from the data.
  const maxDataValue = Math.max(...getDataValuesFromDataValuesAndLabels(dataAndLabels), max);

  // Get the min of user specified min value or the min value from the data.
  const minDataValue = Math.min(...getDataValuesFromDataValuesAndLabels(dataAndLabels), min);
  const maxValue = maxReferenceLineValue ? Math.max(maxDataValue, maxReferenceLineValue) : maxDataValue;
  const minValue = minReferenceLineValue ? Math.min(minDataValue, minReferenceLineValue) : minDataValue;
  return {
    min: minValue,
    max: maxValue
  };
};
const getSeries = (data, layer, events) => {
  const {
    onPointClick
  } = events !== null && events !== void 0 ? events : {};
  const {
    dataColumn,
    compareColumn,
    valueFormattingOptions,
    referenceLineValues
  } = layer;
  const updatedReferenceLineValues = referenceLineValues && referenceLineValues.map(referenceLineValue => Object.assign({}, referenceLineValue, {
    value: referenceLineValue.value && convertNumberIfNecessary(data.primary, layer.dataColumn, referenceLineValue.value, valueFormattingOptions)
  }));
  const dataAndLabels = extractDataValuesAndLabels(data, dataColumn, valueFormattingOptions, compareColumn);

  // Construct data series
  const {
    dataValue,
    dataValueLabel,
    compareValueLabel,
    dataRow,
    dataset
  } = dataAndLabels.primary;
  const updatedEvents = onPointClick ? {
    click() {
      onPointClick({
        row: fromJS(dataRow),
        layer,
        dataset: dataset.data
      });
    }
  } : {};
  const series = Array();
  const primarySeriesName = compareValueLabel ? `${dataValueLabel} - ${compareValueLabel}` : `${dataValueLabel}`;
  const defaultGaugeSeries = {
    type: 'gauge',
    animation: false,
    stickyTracking: false,
    allowPointSelect: false,
    dataLabels: {
      enabled: false
    },
    marker: {
      symbol: 'circle'
    }
  };
  const primarySeries = Object.assign({}, defaultGaugeSeries, {
    name: primarySeriesName,
    data: [dataValue],
    dataLabels: {
      enabled: true,
      useHTML: true,
      overflow: 'allow',
      crop: false,
      formatter: getDataLabelFormatter(data, dataColumn, valueFormattingOptions, compareColumn)
    },
    events: updatedEvents,
    dial: {
      backgroundColor: PRIMARY_SERIES_DEFAULT_COLOR
    },
    color: PRIMARY_SERIES_DEFAULT_COLOR
  });
  series.push(primarySeries);
  if (dataAndLabels.compare) {
    const {
      dataValue: previousValue,
      compareValueLabel: previousValueLabel,
      dataRow: previousDataRow,
      datasetName: previousDatasetName,
      dataset: previousDataset
    } = dataAndLabels.compare;
    const updatedEventsComparedDataset = onPointClick ? {
      click() {
        const updatedLayer = Object.assign({}, layer, previousDatasetName && {
          dataset: previousDatasetName
        });
        onPointClick({
          row: fromJS(previousDataRow),
          layer: updatedLayer,
          dataset: previousDataset === null || previousDataset === void 0 ? void 0 : previousDataset.data
        });
      }
    } : {};
    // Compare
    const compareSerie = Object.assign({}, defaultGaugeSeries, {
      name: `${dataValueLabel} - ${previousValueLabel}`,
      data: [previousValue],
      dataLabels: {
        enabled: false
      },
      events: updatedEventsComparedDataset,
      dial: {
        backgroundColor: COMPARE_SERIES_DEFAULT_COLOR
      },
      color: COMPARE_SERIES_DEFAULT_COLOR
    });
    series.push(compareSerie);
  }
  if (updatedReferenceLineValues && updatedReferenceLineValues.length > 0) {
    const defaultReferenceLineSeries = Object.assign({}, defaultGaugeSeries, {
      name: I18n.text('reporting-visualizations.gauge.reference-line-label'),
      dial: {
        backgroundColor: REFERENCE_LINE_SERIES_DEFAULT_COLOR
      },
      color: REFERENCE_LINE_SERIES_DEFAULT_COLOR
    });
    const referenceLineSeries = updatedReferenceLineValues.filter(referenceLine => isFiniteNumber(referenceLine.value)).map(referenceLineValue => Object.assign({}, defaultReferenceLineSeries, {
      data: referenceLineValue.value ? [referenceLineValue.value] : undefined,
      name: referenceLineValue.label || defaultReferenceLineSeries.name
    }));
    series.push(...referenceLineSeries);
  }
  return series;
};
const pane = {
  background: [],
  startAngle: -90,
  endAngle: 90,
  center: ['50%', '71%'],
  size: '95%'
};
const getBackgroundBand = (from, to) => {
  // Background band
  return {
    from,
    to,
    color: GREAT_WHITE,
    thickness: BAND_THICKNESS_PERCENTAGE
  };
};
const buildYAxis = (data, layer) => {
  const {
    valueFormattingOptions,
    plot = {},
    referenceLineValues
  } = layer;
  const {
    bands = []
  } = plot;
  const updatedReferenceLineValues = referenceLineValues && referenceLineValues.map(referenceLineValue => Object.assign({}, referenceLineValue, {
    value: referenceLineValue.value && convertNumberIfNecessary(data.primary, layer.dataColumn, referenceLineValue.value, valueFormattingOptions)
  }));
  const {
    min,
    max
  } = getGaugeRange(layer, data, updatedReferenceLineValues);
  const plotBands = bands.map(band => {
    // Identify bands from/to can be defined in reverse order (i.e. from 500 to 400),
    // so we locate the upper and lower bounds of the band range to ensure
    // it falls within the gauge range
    //
    // We ultimately change the band from/to back to ascending order in this scenario,
    // but HighCharts renders "from 400 to 500" the same way as it renders
    // "from 500 to 400", so this is a safe transformation
    const bandRangeStartValue = isFiniteNumber(band.from) && isFiniteNumber(band.to) ? Math.min(band.from, band.to) // if from and to are present, take the lower value as the start of the range
    : isFiniteNumber(band.from) ? band.from // if only from is present, take that as the start of the range
    : null; // otherwise, leave it null and it will default to the gauge range start

    const bandRangeEndValue = isFiniteNumber(band.from) && isFiniteNumber(band.to) ? Math.max(band.from, band.to) // if from and to are present, take the higher value as the end of the range
    : isFiniteNumber(band.to) ? band.to // if only to is present, take that as the end of the range
    : null; // otherwise, leave it null and it will default to the gauge range end

    const convertedBandRangeStart = bandRangeStartValue && convertNumberIfNecessary(data.primary, layer.dataColumn, bandRangeStartValue, valueFormattingOptions);
    const convertedBandRangeEnd = bandRangeEndValue && convertNumberIfNecessary(data.primary, layer.dataColumn, bandRangeEndValue, valueFormattingOptions);

    // ensure band range is within the gauge range so it doesn't expand the gauge
    const bandFromOrRangeMin = isFiniteNumber(convertedBandRangeStart) ? Math.min(Math.max(convertedBandRangeStart, min), max) : min;
    const bandToOrRangeMax = isFiniteNumber(convertedBandRangeEnd) ? Math.max(Math.min(convertedBandRangeEnd, max), min) : max;
    const highChartBand = Object.assign({}, band, {
      from: bandFromOrRangeMin,
      to: bandToOrRangeMax,
      thickness: BAND_THICKNESS_PERCENTAGE
    });
    return highChartBand;
  });

  // Add a background band
  plotBands.unshift(getBackgroundBand(min, max));
  const yAxis = {
    min,
    max,
    plotBands,
    tickLength: 0,
    // Hide the ticks
    minorTickInterval: null,
    labels: {
      /* This is distance between the dial and tick label */
      distance: 20,
      style: {
        fontSize: '12px'
      },
      formatter: getAxisLabelFormatter(data.primary, layer.dataColumn, valueFormattingOptions)
    },
    tickPositions: !layer.showRangeLabels ? [min, max] : undefined
  };
  return [yAxis];
};
const getGaugeChartOptions = visualization => {
  const {
    data,
    layer,
    events,
    style
  } = visualization;
  const {
    legend: legendOptions = {},
    chart: legendOffsets = {}
  } = getLegendPositionOptions(style.legendPosition, style.legendWidth);
  const chart = Object.assign({
    type: 'gauge',
    marginTop: 2,
    marginBottom: 25,
    marginLeft: 1,
    marginRight: 1
  }, legendOffsets);
  const legend = Object.assign({}, legendOptions, {
    itemStyle: {
      cursor: 'default'
    }
  });
  const {
    valueFormattingOptions
  } = layer;
  return {
    chart,
    pane,
    yAxis: buildYAxis(data, layer),
    series: getSeries(data, layer, events),
    legend,
    tooltip: {
      formatter: getTooltipFormatter(data, layer.dataColumn, valueFormattingOptions
      /*, layer.compareColumn*/)
    },
    accessibility: {
      point: {
        descriptionFormatter: point => {
          if (!point) {
            return '';
          }
          const seriesName = point.series.name;
          const pointValue = point.y;
          return `${seriesName}: ${pointValue}`;
        }
      }
    }
  };
};
export const getChartOptions = (visualization, debug = DEFAULT_DEBUG, reportingThemes) => {
  const highChartOptions = fromJS(getGaugeChartOptions(visualization.toJS()));
  debug.set('Chart', highChartOptions);
  const defaultOptionsWithGauge = getDefaultOptions({
    reportingThemes
  }).set('plotOptions', fromJS({
    gauge: defaultGaugeSeriesOptions,
    series: {
      states: {
        inactive: {
          opacity: 1
        }
      },
      events: {
        legendItemClick: () => false
      }
    }
  }));
  return defaultOptionsWithGauge.mergeDeep(highChartOptions);
};
export const __TESTABLE__ = {
  convertNumberIfNecessary
};